import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCoreApplicationContext } from "@webcore/store/slices/userContextSlice.ts";
import { CoreApplicationContext, getSessionIdentity, sessionIdentitiesEqual } from "@webcore/models/coreModels.ts";
import { subscribe as subscribeCrossTab } from '@webcore/services/CrossTabSessionChannel';
import { api, useLazyGetCoreApplicationContextQuery } from '@webcore/services/api/api';

// Shared across React apps (web-client, ops-mfe-app). The context value is the CoreApplicationContext
// held in the `coreContext` store slice, populated by each app's auth/bootstrap layer (web-client's
// AuthProvider, the MFE's MfeBootstrap) once its token has been exchanged for a session cookie. This
// provider only exposes the loaded context and reacts to it being lost; it does not perform the
// exchange, so apps differ in how they authenticate but share how the context is consumed.
const CoreApplicationProviderContext = createContext<CoreApplicationContext | undefined>(undefined);

interface CoreApplicationContextProviderProps {
  children: ReactNode;
  // Invoked when the session is lost while mounted (the context becomes null). web-client redirects
  // to login; apps with no login flow (the read-only MFE) omit this and simply render nothing.
  onSessionLost?: () => void;
}

export const CoreApplicationContextProvider: React.FC<CoreApplicationContextProviderProps> = ({ children, onSessionLost }) => {
  const dispatch = useDispatch();
  const coreApplicationContext = useSelector(selectCoreApplicationContext);
  const [getCoreApplicationContext] = useLazyGetCoreApplicationContextQuery();

  useEffect(() => {
    if (coreApplicationContext === null) {
      dispatch(logout());
      onSessionLost?.();
    }
  }, [coreApplicationContext]);

  // Cross-tab session detection: react if another tab on this origin changes session identity.
  useEffect(() => {
    const unsubscribe = subscribeCrossTab(message => {
      if (message.type !== 'SESSION_CHANGED') {
        return;
      }

      const localSessionIdentity = getSessionIdentity(coreApplicationContext);

      if (sessionIdentitiesEqual(localSessionIdentity, message.senderSessionIdentity)) {
        return;
      }

      if ((localSessionIdentity === null) ||
        (localSessionIdentity.userContext.emailAddress !== message.senderSessionIdentity?.userContext.emailAddress) ||
        (localSessionIdentity.userContext.userId !== message.senderSessionIdentity?.userContext.userId)
        ) {
        window.location.href = "/";
        return;
      }

      // Same user, possibly a different team or account — clear cached data and re-bootstrap context.
      dispatch(api.util.resetApiState());

      getCoreApplicationContext().unwrap().then(() => {
      }).catch(() => {
        window.location.href = "/";
      });
    });

    return unsubscribe;
  }, [coreApplicationContext, dispatch, getCoreApplicationContext]);

  if (coreApplicationContext == null) {
    return null;
  }

  return (
      <CoreApplicationProviderContext.Provider value={coreApplicationContext}>
        {children}
      </CoreApplicationProviderContext.Provider>
  );
};

// Custom hook to use the context
export const useCoreApplicationContext = (): CoreApplicationContext => {
  const context = useContext(CoreApplicationProviderContext);

  if (context === undefined) {
    throw new Error('useCoreApplication must be used within a CoreApplicationContextProvider');
  }

  return context;
};
